/**
 * Shared Timezone-Aware Time Utilities
 *
 * All timestamps from the backend are UTC (SQLite CURRENT_TIMESTAMP).
 * These helpers ensure correct conversion to the user's local timezone.
 *
 * Why this matters (for FYP viva):
 * - SQLite stores UTC timestamps (no timezone info)
 * - API returns ISO strings WITHOUT 'Z' suffix
 * - JavaScript new Date("2026-02-06T21:15:00") treats it as LOCAL time (wrong!)
 * - We must append 'Z' so JS knows it's UTC, then toLocaleString auto-converts
 *   to the user's browser timezone (e.g. UTC+8 for Malaysia)
 */

/**
 * Parse a backend UTC timestamp into a proper Date object.
 * Handles: ISO strings with/without 'Z', null/undefined values.
 */
export function parseUTC(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  // If it already has timezone info (Z or +/-offset), parse as-is
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) {
    return new Date(s);
  }
  // Backend returns UTC without 'Z' — append it
  return new Date(s + 'Z');
}

/**
 * Format a UTC timestamp to the user's local date+time.
 * Respects Date/Time Format setting (24h vs 12h) from Settings when set on window.__cdeaas_dateFormat.
 * Example: 24h → "6 Feb 2026, 14:30:00" ; 12h → "6 Feb 2026, 2:30:00 PM"
 */
export function formatDateTime(dateStr) {
  const d = parseUTC(dateStr);
  if (!d || isNaN(d.getTime())) return 'N/A';
  const hour12 = typeof window !== 'undefined' && window.__cdeaas_dateFormat === '12h';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: hour12,
  });
}

/**
 * Format a UTC timestamp to the user's local date only (no time).
 * Example output: "6 Feb 2026"
 */
export function formatDate(dateStr) {
  const d = parseUTC(dateStr);
  if (!d || isNaN(d.getTime())) return 'N/A';
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

/**
 * Format a UTC timestamp to a human-friendly relative time.
 * Example: "Just now", "3m ago", "2h ago", "5d ago"
 */
export function formatRelative(dateStr) {
  const d = parseUTC(dateStr);
  if (!d || isNaN(d.getTime())) return '';
  const now = new Date();
  const diffMs = now - d;
  if (diffMs < 0) return 'Just now';
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}w ago`;
  return formatDate(dateStr);
}

/**
 * Get the user's current timezone name.
 * Example: "Asia/Kuala_Lumpur", "Europe/London"
 */
export function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

/**
 * Get a short timezone label for display.
 * Example: "MYT", "GMT", "EST"
 */
export function getTimezoneLabel() {
  try {
    const d = new Date();
    const parts = d.toLocaleTimeString(undefined, { timeZoneName: 'short' }).split(' ');
    return parts[parts.length - 1]; // e.g. "MYT", "GMT+8"
  } catch {
    return '';
  }
}
